/* eslint-disable camelcase */
import { notifyError } from "../notifyError.js";
import Vue from "vue";
import Vuex from "vuex";
import getUnixTime from "date-fns/getUnixTime";
import { ucfirst } from "../ucfirst.js";
import jwtDecode from "jwt-decode";
import {
  USUARIO_PERMISSAO_MASTER,
  USUARIO_PERMISSAO_ADMINISTRADOR,
  USUARIO_PERMISSAO_MOTORISTA,
  USUARIO_PERMISSAO_VENDEDOR,
  USUARIO_PERMISSAO_INDUSTRIA,
  USUARIO_PERMISSAO_SUPERVISOR,
} from "../consts.js";
import * as idb from "../idb.worker.js";
import { delay } from "src/draf.js";
import { Notify } from "quasar";
import { v4 as uuidv4 } from 'uuid';
// import { diff } from 'deep-object-diff';

const { version } = require("../../package.json");

Vue.use(Vuex);

/*
 * If not building with SSR mode, you can
 * directly export the Store instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Store instance.
 */

const RESET_FORM_USUARIO = {
  id: null,
  empresa_id: null,
  permissao: 2,
  nome: "",
  email: "",
  uid: null,
  cnpj: null,
  cpf: '',
  telefone: "",
  senha: "",
  ativo: true,
  // logo_url: null,
  ultimo_acesso: null,
  info: "{}",
  uids: "[]",
  permissao_id: null,
};

const RESET_FORM_EMPRESA = {
  id: null,
  nome: "",
  api_token: "",
  ativa: true,
  lat: 0,
  lng: 0,
};

const EMPTY_OPTION = { value: 0, label: "--" };

function createMutationMerge(key) {
  return function (state, arr) {
    state[key] = [
      ...state[key].map((u) => arr.find((u2) => u2.id === u.id) ?? u), // update
      ...arr.filter((u) => state[key].findIndex((u2) => u2.id === u.id) === -1), // insert
    ];
  };
}

function createRemoveTemporary(key) {
  return function (state, [__temp]) {
    state[key] = state[key].filter((d) => d.__temp !== __temp);
  };
}

function createReplaceTemporary(key) {
  return function (state, [__temp, data]) {
    state[key] = state[key].map((d) => (d.__temp === __temp ? data : d));
  };
}

function createActionTemporarySync(
  plural,
  singular,
  filter = null,
  map = null
) {
  return async function ({ getters, state, commit }) {
    if (!getters.isMotorista) throw new Error("Você não pode fazer isso");

    let error = null;
    for (const o of state[plural]) {
      if (!("__temp" in o)) continue;
      if (filter && !filter(o)) continue;
      try {
        const data = await this.$srpc(`set_${singular}`, map ? map(o) : o);
        if (data && data.id) {
          commit(`removeTemporary${plural.split("_").map(ucfirst).join("")}`, [
            o.__temp,
            data,
          ]);
        }
      } catch (err) {
        error = err;
      }
    }
    if (error !== null) throw error;
  };
}

function createActionSync(plural, singular) {
  return async function ({ getters, state, commit }, force = false) {
    if (!getters.isMotorista) throw new Error("Você não pode fazer isso");

    const lmat = force
      ? 0
      : state[plural]
        .map((u) => {
          return {
            mat: u.mat,
            time: getUnixTime(new Date(u.mat)),
          };
        })
        .sort((a, b) => a.time - b.time)
        .pop()?.mat;
    const r = await this.$rpc(`list_${plural}`, { lmat });

    if ("data" in r) {
      if (Array.isArray(r.data)) {
        if (r.data.length > 0) {
          commit(`merge${plural.split("_").map(ucfirst).join("")}`, r.data);
        }
      } else {
        commit(`merge${plural.split("_").map(ucfirst).join("")}`, r.data);
      }
    }

    const ids = state[plural].map((p) => p.id);
    const x = await this.$rpc("need_remove", { list: plural, ids });
    if (x.data && x.data.length > 0) {
      commit("setAny", {
        key: plural,
        value: state[plural].filter((a) => x.data.indexOf(a.id) === -1),
      });
    }
  };
}

function createActionSync2(plural, singular) {
  return async function ({ getters, state, commit }, force = false) {
    const lmat = force
      ? 0
      : state[plural]
        .map((u) => ({
          mat: u.mat,
          time: getUnixTime(new Date(u.mat)),
        }))
        .sort((a, b) => a.time - b.time)
        .pop()?.mat || 0;

    let $skip = 0;
    let r;
    const data = [];
    const $limit = 100;
    const maximumIterations = 1000;
    let iterations = 0;
    do {
      r = await this.$srpc(`${plural}/find`, {
        query: {
          $limit,
          $sort: { mat: 1 },
          mat: { $gt: lmat },
          dat: "0000-00-00 00:00:00",
          $skip,
        },
      });
      data.push(...r.data);
      $skip += $limit;
      if (++iterations >= maximumIterations) {
        notifyError("Limite de iterações atingido!");
        break;
      }
    } while ($skip < r.total);

    if (data.length > 0) {
      commit(`merge${plural.split("_").map(ucfirst).join("")}`, data);
    }

    if (getters.isMotorista) {
      const ids = state[plural].map((p) => p.id);
      const x = await this.$rpc("need_remove", { list: plural, ids });
      if (x.data && x.data.length > 0) {
        commit("setAny", {
          key: plural,
          value: state[plural].filter((a) => x.data.indexOf(a.id) === -1),
        });
      }
    }
  };
}

function createGetterBy(plural, prop) {
  return function (state) {
    return state[plural].reduce((r, c) => {
      r[c[prop]] = c;
      return r;
    }, {});
  };
}

function createGetBy(key, prop) {
  return function (state) {
    return (val) => {
      const vals = state[key].filter((d) => d[prop] === val);
      console.assert(
        vals.length === 1,
        `found ${vals.length} state.${key}.${prop} = ${val}`
      );
      if (vals.length > 1) console.log(vals);
      return vals[0];
    };
  };
}

const createState = () => ({
  pageTitle: "Gestão de Entregas",
  pageHandleBack: null,
  focused: true,
  connected: window.navigator.onLine,
  version,
  serverVersion: "0.0.0",
  accessToken: undefined,
  rastreadorAccessToken: undefined,
  empresaId: null,
  synchronizing: false,

  showDialogUsuario: false,
  formDialogUsuario: { ...RESET_FORM_USUARIO },

  detalhesPedidoId: null,

  notificacoes: [],
  notificacoesTotal: 0,
  loadingNotificacoes: false,

  empresas: [],
  loadingEmpresas: false,
  showDialogEmpresa: false,
  formDialogEmpresa: { ...RESET_FORM_EMPRESA },

  loadingClientes: false,
  loadingVeiculos: false,

  loadingMotivos: false,
  loadingItens: false,

  loadingUsuarios: false,
  loadingChecagens: false,
  loadingOcorrencias: false,
  loadingAjustes: false,

  initialisedStore: false,

  permissoes: undefined,

  usuarios: [],
  veiculos: [],
  viagens: [],
  checagens: [],
  ocorrencias: [],
  cargas: [],
  pedidos: [],
  clientes: [],
  entregas: [],
  upsert_entregas: [],
  entregas_imagens: [],
  itens: [],
  motivos: [],
  viagem_carga: [],
  emissores: [],
  ajustes: [],

  writingAccesstoken: false,
  writingEmpresaid: false,
  writingEmpresas: false,
  writingUsuarios: false,
  writingVeiculos: false,
  writingViagens: false,
  writingChecagens: false,
  writingOcorrencias: false,
  writingCargas: false,
  writingPedidos: false,
  writingClientes: false,
  writingEntregas: false,
  writingItens: false,
  writingMotivos: false,
  writingViagem_carga: false,
  writingEntregas_imagens: false,
  writingUpsert_entregas: false,
  writingEmissores: false,
  writingAjustes: false,
});

export default async function (/* { ssrContext } */) {
  const persistKeys = ["accessToken", "empresaId"];

  const persistAllKeys = [
    ...persistKeys,
    "empresas",
    "usuarios",
    "veiculos",
    "viagens",
    "checagens",
    "ocorrencias",
    "cargas",
    "pedidos",
    "clientes",
    "entregas",
    "upsert_entregas",
    "entregas_imagens",
    "itens",
    "motivos",
    "viagem_carga",
    "emissores",
    "ajustes",
  ];

  const Store = new Vuex.Store({
    modules: {},
    state: createState(),

    actions: {
      async refreshRastreadorLogin({ commit, getters }) {
        try {
          if (!getters.empresaId) return null;
          const data = await this.$srpc('rastreador_login', { empresa_id: getters.empresaId });
          commit('setRastreadorAccessToken', data.accessToken);
        } catch (err) {
          notifyError(err);
          commit('setRastreadorAccessToken', '');
        }
      },
      async safePutEntrega({ commit, state, dispatch }, entrega) {
        commit("setEntregaImagens", JSON.parse(JSON.stringify(entrega)));

        const oldEntrega =
          entrega.id || entrega.__temp
            ? state.entregas.find((e) =>
              entrega.id ? e.id === entrega.id : e.__temp === entrega.__temp
            )
            : null;

        if (oldEntrega !== null) {
          const difEntrega = Object.keys(entrega).reduce(
            (dif, key) => {
              if (Array.isArray(entrega[key])) {
                entrega[key] = entrega[key].filter((val) => oldEntrega[key].indexOf(val) === -1);
              } else if (entrega[key] !== oldEntrega[key]) {
                dif[key] = entrega[key];
              }
              return dif;
            },
            {
              viagem_id: entrega.viagem_id,
              pedido_id: entrega.pedido_id,
              imagens: [...(entrega.imagens || []), ...(oldEntrega.imagens || [])]
            }
          );
          if (entrega.id) difEntrega.id = entrega.id;
          commit("upsertEntrega", difEntrega);
        } else {
          commit("upsertEntrega", entrega);
        }

        commit("setWriting", ["entregas", true]);
        commit("putEntrega", entrega);

        do {
          await delay();
        } while (state.writingEntregas);

        dispatch("sync", false);
      },
      async logout({ commit }) {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key.indexOf("checagem/") > -1) {
            localStorage.removeItem(key);
          }
        }
        commit("reset");
      },
      // async logoutVeiculo({ commit, dispatch, state }) {
      //   const r = await this.$rpc("sair_do_veiculo", {});
      //   if (r.data) {
      //     commit("reset");
      //     commit("setLogin", r.data);

      //     dispatch('sync');
      //   } else {
      //     throw new Error(r.error);
      //   }
      // },
      async loginVeiculo({ commit, dispatch, state }, qrcode) {
        const r = await this.$rpc("login_veiculo", { qrcode });
        if ("data" in r) {
          // commit("reset");
          commit("setLogin", r.data);

          await dispatch("sync");
        } else {
          throw new Error(r.error);
        }
      },
      login({ state, commit, dispatch }, params) {
        return this.$rpc("login", params).then((r) => {
          if ("data" in r) {
            commit("reset");
            commit("setLogin", r.data);

            dispatch("sync");
          } else throw new Error(r.error);
        });
      },
      // eslint-disable-next-line
      async loadEmpresas({ commit }, { fields } = {}) {
        commit("setLoadingEmpresas", true);
        try {
          const r = await this.$rpc("list_empresas", { fields });
          if ("data" in r) commit("setEmpresas", r.data);
          else if (r.error !== "Login obrigatório") alert(r.error);
        } catch (err) {
          notifyError(err);
        }
        commit("setLoadingEmpresas", false);
      },
      // eslint-disable-next-line
      async loadClientes({ commit }, { empresa_id, fields } = {}) {
        commit("setLoadingClientes", true);
        try {
          const r = await this.$rpc("list_clientes", { empresa_id, fields });
          if ("data" in r) commit("setClientes", r.data);
          else if (r.error !== "Login obrigatório") alert(r.error);
        } catch (err) {
          notifyError(err);
        }
        commit("setLoadingClientes", false);
      },
      // eslint-disable-next-line
      async loadVeiculos({ commit }, { empresa_id, fields } = {}) {
        commit("setLoadingVeiculos", true);
        try {
          const r = await this.$rpc("list_veiculos", { empresa_id, fields });
          if ("data" in r) commit("setVeiculos", r.data);
          else if (r.error !== "Login obrigatório") alert(r.error);
        } catch (err) {
          notifyError(err);
        }
        commit("setLoadingVeiculos", false);
      },
      // eslint-disable-next-line
      async loadItens({ commit }, empresa_id) {
        commit("setLoadingItens", true);
        try {
          const r = await this.$rpc("list_itens", { empresa_id });
          if ("data" in r) commit("setItens", r.data);
          else if (r.error !== "Login obrigatório") alert(r.error);
        } catch (err) {
          notifyError(err);
        }
        commit("setLoadingItens", false);
      },
      // eslint-disable-next-line
      async loadMotivos({ commit }, empresa_id) {
        commit("setLoadingMotivos", true);
        try {
          const r = await this.$rpc("list_motivos", { empresa_id });
          if ("data" in r) commit("setMotivos", r.data);
          else if (r.error !== "Login obrigatório") alert(r.error);
        } catch (err) {
          notifyError(err);
        }
        commit("setLoadingMotivos", false);
      },
      // eslint-disable-next-line
      async loadChecagens({ commit }, empresa_id) {
        commit("setLoadingChecagens", true);
        try {
          const r = await this.$rpc("list_checagens", { empresa_id });
          if ("data" in r) commit("setChecagens", r.data);
          else if (r.error !== "Login obrigatório") alert(r.error);
        } catch (err) {
          notifyError(err);
        }
        commit("setLoadingChecagens", false);
      },
      // eslint-disable-next-line
      async loadOcorrencias({ commit }, empresa_id) {
        commit("setLoadingOcorrencias", true);
        try {
          const r = await this.$rpc("list_ocorrencias", { empresa_id });
          if ("data" in r) commit("setOcorrencias", r.data);
          else if (r.error !== "Login obrigatório") alert(r.error);
        } catch (err) {
          notifyError(err);
        }
        commit("setLoadingOcorrencias", false);
      },
      // eslint-disable-next-line
      async loadNotificacoes({ commit, getters }) {
        if (!getters.empresaId || !getters.usuario) return;
        commit("setLoadingNotificacoes", true);
        try {
          const query = {
            lida: 0,
            empresa_id: getters.empresaId,
            // usuario_id: getters.usuario.id,
            $limit: 100,
          };
          const r = await this.$srpc("notificacoes/find", { query });
          commit("setNotificacoes", [r.data, r.total]);
        } catch (err) {
          notifyError(err);
        }
        commit("setLoadingNotificacoes", false);
      },
      async loadPermissoes({ getters, commit }) {
        const r = await this.$srpc("permissoes/find", {
          query: {
            empresa_id: getters.empresaId,
            dat: '0000-00-00 00:00:00',
            $limit: 1000,
          }
        });
        commit('setPermissoes', r.data);
      },
      async loadUsuarios({ getters, commit }) {
        if (getters.isMotorista) throw new Error("Você não pode fazer isso");
        commit("setLoadingUsuarios", true);

        const r = await this.$rpc("list_usuarios", {});

        // console.log(r)
        if ("data" in r) commit("setUsuarios", r.data);
        else if (r.error !== "Login obrigatório") alert(r.error);

        commit("setLoadingUsuarios", false);
      },

      syncTemporaryUpsertEntregas: createActionTemporarySync(
        "upsert_entregas",
        "upsert_entrega"
      ),
      syncTemporaryEntregasImagens: createActionTemporarySync(
        "entregas_imagens",
        "entrega_imagem"
      ),
      syncTemporaryOcorrencias: createActionTemporarySync(
        "ocorrencias",
        "ocorrencia"
      ),

      syncEmpresas: createActionSync("empresas", "empresa"),
      syncUsuarios: createActionSync("usuarios", "usuario"),
      syncVeiculos: createActionSync("veiculos", "veiculo"),
      syncViagens: createActionSync("viagens", "viagem"),
      syncChecagens: createActionSync("checagens", "checagem"),
      syncOcorrencias: createActionSync("ocorrencias", "ocorrencia"),
      syncCargas: createActionSync("cargas", "carga"),
      syncPedidos: createActionSync("pedidos", "pedido"),
      syncClientes: createActionSync("clientes", "cliente"),
      syncEntregas: createActionSync("entregas", "entrega"),
      syncItens: createActionSync("itens", "item"),
      syncMotivos: createActionSync("motivos", "motivo"),
      syncViagemCarga: createActionSync("viagem_carga", "viagem_carga"),
      syncEmissores: createActionSync2("emissores", "emissor"),
      syncAjustes: createActionSync2("ajustes", "ajuste"),

      async sync({ dispatch, commit, state }, all = true) {
        if (state.synchronizing) return;
        try {
          // console.time(`[sync]${all ? ' all' : ''}`);
          commit("setSynchronizing", true);

          await dispatch("syncTemporaryUpsertEntregas");
          await dispatch("syncTemporaryEntregasImagens");
          await dispatch("syncTemporaryOcorrencias");

          if (all) {
            await Promise.all([
              dispatch("syncEmpresas"),
              dispatch("syncUsuarios"),
              dispatch("syncVeiculos"),
              dispatch("syncViagens"),
              dispatch("syncChecagens"),
              dispatch("syncCargas"),
              dispatch("syncPedidos"),
              dispatch("syncClientes"),
              dispatch("syncItens"),
              dispatch("syncMotivos"),
              dispatch("syncViagemCarga"),
              dispatch("syncEmissores"),
              dispatch("syncAjustes"),
              dispatch("syncOcorrencias"),
              dispatch("syncEntregas"),
            ]);
          } else {
            await dispatch("syncEntregas");
          }
        } catch (err) {
          console.error(err);
          const message = 'message' in err ? err.message : '' + err;
          if (!message.includes("Login com veículo obrigatório")) {
            if (message.includes('[API]')) {
              Notify.create({ type: "negative", message });
            }
          }
        } finally {
          commit("setSynchronizing", false);
          // console.timeEnd(`[sync]${all ? ' all' : ''}`);
        }
      },
    },

    getters: {
      writing(state) {
        return (
          state.writingAccesstoken ||
          state.writingEmpresaid ||
          state.writingEmpresas ||
          state.writingUsuarios ||
          state.writingVeiculos ||
          state.writingViagens ||
          state.writingChecagens ||
          state.writingOcorrencias ||
          state.writingCargas ||
          state.writingPedidos ||
          state.writingClientes ||
          state.writingEntregas ||
          state.writingItens ||
          state.writingMotivos ||
          state.writingViagem_carga ||
          state.writingEntregas_imagens ||
          state.writingUpsert_entregas ||
          state.writingEmissores ||
          state.writingAjustes
        );
      },
      hasNewVersion(state) {
        if (state.serverVersion === "0.0.0") return false;

        const cv = state.version.split(".").map((v) => parseInt(v, 10));
        const sv = state.serverVersion.split(".").map((v) => parseInt(v, 10));

        if (sv[0] > cv[0]) return true;
        else if (sv[0] < cv[0]) return false;
        else if (sv[1] > cv[1]) return true;
        else if (sv[1] < cv[1]) return false;
        else if (sv[2] > cv[2]) return true;
        else if (sv[2] < cv[2]) return false;
        return false;
      },
      appIsLoading(state) {
        return state.accessToken === undefined;
      },
      loggedIn(state) {
        return state.accessToken ? jwtDecode(state.accessToken) : false;
      },
      isMotorista(state, getters) {
        return (
          getters.loggedIn &&
          getters.loggedIn.pid === USUARIO_PERMISSAO_MOTORISTA
        );
      },
      isAdministrador(state, getters) {
        return (
          getters.loggedIn &&
          getters.loggedIn.pid === USUARIO_PERMISSAO_ADMINISTRADOR
        );
      },
      isVendedor(state, getters) {
        return (
          getters.loggedIn &&
          getters.loggedIn.pid === USUARIO_PERMISSAO_VENDEDOR
        );
      },
      isIndustria(state, getters) {
        return (
          getters.loggedIn &&
          getters.loggedIn.pid === USUARIO_PERMISSAO_INDUSTRIA
        );
      },
      isSupervisor(state, getters) {
        return (
          getters.loggedIn &&
          getters.loggedIn.pid === USUARIO_PERMISSAO_SUPERVISOR
        );
      },
      isMaster(state, getters) {
        return (
          getters.loggedIn && getters.loggedIn.pid === USUARIO_PERMISSAO_MASTER
        );
      },

      toWrite(state, getters) {
        const keys = getters.isMotorista ? persistAllKeys : persistKeys;

        return keys.reduce((r, k) => {
          r[k] = state[k];
          return r;
        }, {});
      },

      usuario(state, getters) {
        const id =
          getters.loggedIn && getters.loggedIn.uid
            ? getters.loggedIn.uid
            : null;
        return id ? state.usuarios.find((v) => v.id === id) : null;
      },
      empresaId(state, getters) {
        return getters.usuario && getters.usuario.empresa_id !== null
          ? getters.usuario.empresa_id
          : state.empresaId;
      },
      empresa(state, getters) {
        return getters.empresaId
          ? state.empresas.find((e) => e.id === getters.empresaId)
          : null;
      },
      veiculo(state, getters) {
        const id =
          getters.loggedIn && getters.loggedIn.vid
            ? getters.loggedIn.vid
            : null;
        return id ? state.veiculos.find((v) => v.id === id) : null;
      },
      viagem(state, getters) {
        const id =
          getters.loggedIn && getters.loggedIn.vid
            ? getters.loggedIn.vid
            : null;
        return id ? state.viagens.find((v) => v.veiculo_id === id) : null;
      },
      carga(state, getters) {
        const id =
          getters.viagem && getters.viagem.carga_id
            ? getters.viagem.carga_id
            : null;
        return id ? state.cargas.find((c) => c.id === id) : null;
      },
      motivosById: createGetterBy("motivos", "id"),

      getUsuarioByUID: createGetBy("usuarios", "uid"),
      getUsuarioById: createGetBy("usuarios", "id"),
      getClienteById: createGetBy("clientes", "id"),
      getPedidoById: createGetBy("pedidos", "id"),
      getEmpresaById: createGetBy("empresas", "id"),
      getVeiculoById: createGetBy("veiculos", "id"),
      getItemById: createGetBy("itens", "id"),
      getEntregaByPedidoId: createGetBy("entregas", "pedido_id"),
      getEmissorByCNPJ: createGetBy("emissores", "cnpj"),

      getPedidosByClienteId(state) {
        return (clienteId, pedidoId) =>
          state.pedidos.find(
            (p) => p.cliente_id === clienteId && p.id !== pedidoId
          );
      },

      getPedidoIdOfEntregaEmAndamento(state) {
        return () =>
          state.entregas.find((e) => e.iniciada_em && e.concluida_em === null)
            ?.pedido_id;
      },

      hasTemporary(state) {
        const keys = [
          // "entregas",
          "upsert_entregas",
          "ocorrencias",
          "entregas_imagens",
        ];
        let hasTemporary = false;
        for (const k of keys) {
          for (const d of state[k]) {
            if ("__temp" in d) {
              hasTemporary = true;
              break;
            }
          }
          if (hasTemporary) break;
        }
        return hasTemporary;
      },

      empresasOptions(state) {
        return [
          EMPTY_OPTION,
          ...state.empresas.map((u) => ({
            value: u.id,
            label: u.nome,
          })),
        ];
      },

      permissoesLoaded(state) {
        return state.permissoes !== undefined;
      },
      permissoesDoUsuario(state, getters) {
        const id = getters.usuario?.permissao_id;
        // alert(JSON.stringify(getters.usuario));
        const permissoes = state.permissoes?.find(p => p.id === id)?.permissoes ?? null;
        return permissoes ? JSON.parse(permissoes) : null;
      },
      temPermissao(state, getters) {
        if (getters.usuario && getters.usuario.permissao === 1 && getters.usuario.permissao_id === null) {
          return () => true;
        }
        return (s) =>
          state.permissoes === undefined
            ? false
            : (
              getters.permissoesDoUsuario === null
                ? true
                : getters.permissoesDoUsuario.includes(s)
            );
      }
    },

    mutations: {
      setDetalhesPedidoId(state, id) {
        state.detalhesPedidoId = id;
      },
      setEntregaImagens(state, entrega) {
        if (!entrega.viagem_id) {
          throw new Error("entrega.viagem_id is required");
        }
        if (!entrega.pedido_id) {
          throw new Error("entrega.pedido_id is required");
        }
        if (entrega.imagens && Array.isArray(entrega.imagens)) {
          for (const imagem of entrega.imagens) {
            if (!imagem.data) continue;
            const exists = state.entregas_imagens.find(
              (ei) =>
                ei.viagem_id === entrega.viagem_id &&
                ei.pedido_id === entrega.pedido_id &&
                ei.legenda === imagem.legenda
            );
            if (!exists) {
              state.entregas_imagens = [
                ...state.entregas_imagens,
                {
                  __temp: uuidv4(),
                  __at: Date.now(),
                  ...imagem,
                  viagem_id: entrega.viagem_id,
                  pedido_id: entrega.pedido_id,
                },
              ];
            }
          }
        }
      },
      upsertEntrega(state, entrega) {
        if (!entrega.viagem_id) {
          throw new Error("entrega.viagem_id is required");
        }
        if (!entrega.pedido_id) {
          throw new Error("entrega.pedido_id is required");
        }
        // if (!entrega.id) console.warn('entrega.id without id');
        const val = {
          ...entrega,
          __temp: uuidv4(),
          __at: Date.now(),
        };
        if (val.imagens) delete val.imagens;
        state.upsert_entregas = [...state.upsert_entregas, val];
      },

      setWriting(state, [key, val]) {
        const s = "writing" + ucfirst(key.toLowerCase());
        console.assert(s in state, `${s} not exists in state`);
        state[s] = val;
      },

      setServerVersion(state, v) {
        state.serverVersion = v;
      },

      setPage(state, { title, handleBack }) {
        state.pageTitle = title;
        state.pageHandleBack = handleBack;
      },
      setSynchronizing(state, synchronizing) {
        state.synchronizing = synchronizing;
      },

      setRastreadorAccessToken(state, accessToken) {
        state.rastreadorAccessToken = accessToken;
      },

      mergeEmpresas: createMutationMerge("empresas"),
      mergeUsuarios: createMutationMerge("usuarios"),
      mergeVeiculos: createMutationMerge("veiculos"),
      mergeViagens: createMutationMerge("viagens"),
      mergeChecagens: createMutationMerge("checagens"),
      mergeOcorrencias: createMutationMerge("ocorrencias"),
      mergeCargas: createMutationMerge("cargas"),
      mergePedidos: createMutationMerge("pedidos"),
      mergeClientes: createMutationMerge("clientes"),
      mergeEntregas(state, arr) {
        const notExists = ({ id, pedido_id, viagem_id }) =>
          state.entregas.findIndex(
            (e) =>
              e.id === id ||
              (e.pedido_id === pedido_id && e.viagem_id === viagem_id)
          ) === -1;

        state.entregas = [
          ...arr.filter(notExists), // insert
          ...state.entregas.map((entrega) => {
            const hasUpsertPending =
              state.upsert_entregas.filter(
                (ue) =>
                  ue.pedido_id === entrega.pedido_id &&
                  ue.viagem_id === entrega.viagem_id
              ).length > 0;
            if (hasUpsertPending) return entrega;
            const hasImagesPending =
              state.entregas_imagens.filter(
                (ue) =>
                  ue.pedido_id === entrega.pedido_id &&
                  ue.viagem_id === entrega.viagem_id
              ).length > 0;
            if (hasImagesPending) return entrega;
            const found = arr.find(
              (e) =>
                e.id === entrega.id ||
                (entrega.pedido_id === e.pedido_id &&
                  entrega.viagem_id === e.viagem_id)
            );
            return found || entrega;
          }), // update
        ];
      },
      mergeItens: createMutationMerge("itens"),
      mergeMotivos: createMutationMerge("motivos"),
      mergeViagemCarga: createMutationMerge("viagem_carga"),
      mergeEmissores: createMutationMerge("emissores"),
      mergeAjustes: createMutationMerge("ajustes"),

      removeTemporaryEntregas: createReplaceTemporary("entregas"),
      removeTemporaryOcorrencias: createReplaceTemporary("ocorrencias"),
      removeTemporaryUpsertEntregas: createRemoveTemporary("upsert_entregas"),
      removeTemporaryEntregasImagens: createRemoveTemporary("entregas_imagens"),

      gainedFocus(state) {
        state.focused = true;
      },
      lostFocus(state) {
        state.focused = false;
      },
      gainedConnection(state) {
        state.connected = true;
      },
      lostConnection(state) {
        state.connected = false;
      },
      reset(state) {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key.indexOf("checagem/") > -1) {
            let formItem = localStorage.getItem(key);
            if (formItem) {
              formItem = JSON.parse(formItem);
              if (1000 * 60 * 30 < Date.now() - formItem._at) {
                localStorage.removeItem(key);
              }
            }
          }
        }
        idb.clear();
        const newState = createState();
        for (const key in newState) {
          if (key === "initialisedStore") continue;
          state[key] = key === "accessToken" ? null : newState[key];
        }
      },
      setLogin(state, { accessToken }) {
        state.accessToken = accessToken;
        state.cargas = [];
        state.veiculos = [];
        state.viagens = [];
        state.checagens = [];
        state.ocorrencias = [];
        state.cargas = [];
        state.pedidos = [];
        state.clientes = [];
        state.entregas = [];
        state.viagem_carga = [];

        state.notificacoes = [];
        state.empresas = [];

        state.usuarios = [];
        state.itens = [];
        state.motivos = [];
        state.emissores = [];

        state.upsert_entregas = [];
      },

      putEntrega(state, entrega) {
        let fn = null;

        if (!("__temp" in entrega)) {
          entrega.__temp = uuidv4();
        }

        if ("id" in entrega) {
          fn = (e) => !("id" in e) || e.id !== entrega.id;
        } else {
          fn = (e) => !("__temp" in e) || e.__temp !== entrega.__temp;
        }

        state.entregas = [...state.entregas.filter(fn), entrega];
      },

      putOcorrencia(state, ocorrencia) {
        let fn = null;

        ocorrencia.__temp = uuidv4();

        if ("id" in ocorrencia) {
          fn = (e) => !("id" in e) || e.id !== ocorrencia.id;
        } else {
          fn = (e) => !("__temp" in e) || e.__temp !== ocorrencia.__temp;
        }

        state.ocorrencias = [...state.ocorrencias.filter(fn), ocorrencia];
      },

      hideDialogUsuario(state) {
        state.formDialogUsuario = { ...RESET_FORM_USUARIO };
        state.showDialogUsuario = false;
      },

      hideDialogEmpresa(state) {
        state.formDialogEmpresa = { ...RESET_FORM_EMPRESA };
        state.showDialogEmpresa = false;
      },

      editUsuario(state, row) {
        state.formDialogUsuario = {
          id: row.id,
          nome: row.nome,
          empresa_id: row.empresa_id,
          permissao: row.permissao,
          email: row.email,
          telefone: row.telefone,
          cnpj: row.cnpj,
          cpf: row.cpf,
          uid: row.uid,
          senha: row.senha,
          ativo: row.ativo,
          // logo_url: row.logo_url,
          ultimo_acesso: row.ultimo_acesso || null,
          info: row.info || "{}",
          uids: row.uids || "[]",
          permissao_id: row.permissao_id || null,
        };
        state.showDialogUsuario = true;
      },
      novoUsuario(state) {
        state.showDialogUsuario = true;
      },

      editEmpresa(state, row) {
        state.formDialogEmpresa = {
          id: row.id,
          nome: row.nome,
          api_token: row.api_token,
          ativa: row.ativa,
          lat: row.lat,
          lng: row.lng,
        };
        state.showDialogEmpresa = true;
      },
      novaEmpresa(state) {
        state.showDialogEmpresa = true;
      },

      setVeiculos(state, veiculos) {
        state.veiculos = veiculos;
      },
      setLoadingVeiculos(state, loadingVeiculos) {
        state.loadingVeiculos = loadingVeiculos;
      },

      setLoadingUsuarios(state, loadingUsuarios) {
        state.loadingUsuarios = loadingUsuarios;
      },

      setItens(state, itens) {
        state.itens = itens;
      },
      setMotivos(state, motivos) {
        state.motivos = motivos;
      },
      setAny(state, { key, value }) {
        state[key] = value;
      },
      setClientes(state, clientes) {
        state.clientes = clientes;
      },
      setLoadingItens(state, loadingItens) {
        state.loadingItens = loadingItens;
      },
      setLoadingMotivos(state, loadingMotivos) {
        state.loadingMotivos = loadingMotivos;
      },
      setLoadingChecagens(state, loadingChecagens) {
        state.loadingChecagens = loadingChecagens;
      },
      setLoadingOcorrencias(state, loadingOcorrencias) {
        state.loadingOcorrencias = loadingOcorrencias;
      },
      setUsuarios(state, usuarios) {
        state.usuarios = usuarios;
      },
      setNotificacoes(state, [notificacoes, notificacoesTotal]) {
        state.notificacoes = notificacoes;
        state.notificacoesTotal = notificacoesTotal;
      },
      setLoadingNotificacoes(state, loadingNotificacoes) {
        state.loadingNotificacoes = loadingNotificacoes;
      },
      setPermissoes(state, permissoes) {
        state.permissoes = permissoes;
      },
      setEmpresas(state, empresas) {
        state.empresas = empresas;
      },
      setEmpresaId(state, empresaId) {
        state.empresaId = empresaId;
      },
      setLoadingEmpresas(state, loadingEmpresas) {
        state.loadingEmpresas = loadingEmpresas;
      },

      setLoadingClientes(state, loadingClientes) {
        state.loadingClientes = loadingClientes;
      },

      setInitialisedStore(state) {
        state.initialisedStore = true;
      },

      async initialiseStore(state) {
        const newState = { ...state };
        try {
          const keys = await idb.keys();
          await Promise.all(
            keys.reduce((promises, k) => {
              if (persistAllKeys.indexOf(k) !== -1) {
                promises.push(
                  idb.getItem(k).then((r) => {
                    newState[k] = r;
                  })
                );
              }
              return promises;
            }, [])
          );
        } catch (err) {
          notifyError(
            `Não foi possível carregar os dados do dispositivo. ${err}`
          );
        } finally {
          if (newState.accessToken === undefined) newState.accessToken = null;
          this.replaceState(newState);
          setTimeout(() => Store.commit("setInitialisedStore"), 200);
        }
      },
    },

    // enable strict mode (adds overhead!)
    // for dev mode only
    strict: process.env.DEV,
  });

  Store.commit("initialiseStore");

  return Store;
}
